import { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession, buildSignInUrl } from '../hooks/useSession';

/* Small chip-style auth indicator for Intraop.
 *
 * - Signed out → "Sign in" link that sends the user to the planner's
 *   login page, bringing them back here on success.
 * - Signed in  → email (truncated) that opens the in-app Account
 *   settings page. Sign-out and account deletion live there now so
 *   the home screen stays focused on measurement, not on
 *   account chrome.
 *
 * Designed to be unobtrusive on small screens — sits in a corner,
 * never blocks measurement workflows. */

interface Props {
  /** Optional override for label colour (defaults to muted text). */
  color?: string;
  style?: CSSProperties;
}

export function AuthBadge({ color = 'rgba(245, 245, 247, 0.62)', style }: Props) {
  const { session, loading } = useSession();
  const nav = useNavigate();

  if (loading) {
    return null; // Don't flash a "Sign in" link before we know the state.
  }

  if (!session) {
    return (
      <a
        href={buildSignInUrl()}
        style={{ ...linkStyle, color, ...style }}
        aria-label="Sign in"
      >
        Sign in →
      </a>
    );
  }

  const email = session.user.email ?? 'Signed in';
  // Truncate long emails so the chip stays compact.
  const display = email.length > 24 ? email.slice(0, 22) + '…' : email;

  return (
    <button
      type="button"
      onClick={() => nav('/app/account')}
      style={{
        ...linkStyle,
        color,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        ...style,
      }}
      aria-label={`Account settings for ${email}`}
      title={email}
    >
      <span aria-hidden style={{ color: '#30d158', fontSize: 10 }}>●</span>
      <span>{display}</span>
      <span aria-hidden style={{ opacity: 0.6, fontSize: 14 }}>›</span>
    </button>
  );
}

const linkStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: '6px 4px',
  font: 'inherit',
  fontSize: 13,
  textDecoration: 'none',
};
